<template>
	<div
		ref="target"
		role="alert"
		class="relative flex flex-col justify-center cursor-default mb-6 py-4 pr-11 w-[400px] min-h-[100px] h-max border border-dj-gray-light rounded-normal bg-white shadow-md"
	>
		<span
			v-if="!dialog"
			class="material-icons-outlined text-dj-gray absolute top-4 right-4 cursor-pointer"
			@click="closeSelf"
		>
			close
		</span>
		<div class="flex items-center">
			<template v-if="type === 'success'">
				<div
					class="flex items-center justify-center rounded-full w-dj-60 h-dj-60 ml-5 bg-dj-green bg-opacity-10"
				>
					<span class="material-icons-outlined text-dj-green">check_circle</span>
				</div>
			</template>
			<template v-else-if="type === 'error'">
				<div class="flex items-center justify-center rounded-full w-dj-60 h-dj-60 ml-5 bg-dj-red bg-opacity-10">
					<span class="material-icons-outlined text-dj-red">report_problem</span>
				</div>
			</template>
			<template v-else-if="type === 'info'">
				<div
					class="flex items-center justify-center rounded-full w-dj-60 h-dj-60 ml-5 bg-dj-blue-dark bg-opacity-10"
				>
					<span class="material-icons-outlined text-dj-blue-dark">info</span>
				</div>
			</template>
			<div class="flex flex-col ml-5">
				<span v-if="message && message.length > 0" class="font-medium">{{ message }}</span>
				<span v-if="description && description.length > 0" class="text-sm text-dj-gray">{{ description }}</span>
			</div>
		</div>
		<div v-if="dialog" class="flex space-x-2 mt-4 ml-dj-100">
			<BaseButton size="small" @click="callOnConfirm">{{ confirmText }}</BaseButton>
			<BaseButton type="tertiary" size="small" class="font-medium" @click="callOnCancel">
				{{ cancelText }}
			</BaseButton>
		</div>
	</div>
</template>

<script lang="ts">
import BaseButton from "~/ui-kit/components/BaseButton.vue";
import { defineComponent, onMounted, ref } from "vue";
import { hideToast } from "~/ui-kit/utils/useToast";
import gsap from "gsap";

export default defineComponent({
	name: "BaseToast",
	components: { BaseButton },
	props: {
		toastId: {
			type: String,
			required: true,
		},
		index: {
			type: Number,
			required: true,
		},
		message: {
			type: String,
			required: true,
		},
		description: {
			type: String,
		},
		type: {
			type: String,
			default: "info",
		},
		dialog: {
			type: Boolean,
			default: false,
		},
		cancelText: {
			type: String,
			default: "Cancel",
		},
		confirmText: {
			type: String,
			default: "Confirm",
		},
		onConfirm: {
			type: Function,
			default: () => true,
		},
		onCancel: {
			type: Function,
			default: () => true,
		},
	},
	setup(props) {
		const target = ref<HTMLElement | null>(null);
		let timer: ReturnType<typeof setTimeout>;

		onMounted(() => {
			gsap.from(target.value, {
				y: -100,
			});

			if (!props.dialog) {
				timer = setTimeout(() => {
					gsap.to(target.value, {
						x: 100,
						opacity: 0,
						onComplete: () => {
							clearTimeout(timer);
							hideToast(props.toastId);
						},
					});
				}, 2500);
			}
		});

		function closeSelf() {
			clearTimeout(timer);
			gsap.to(target.value, {
				x: 100,
				opacity: 0,
				display: "none",
				onComplete: () => {
					hideToast(props.toastId);
				},
			});
		}

		function callOnConfirm() {
			props.onConfirm();
			gsap.to(target.value, {
				x: -100,
				opacity: 0,
				onComplete: () => {
					closeSelf();
				},
			});
		}

		function callOnCancel() {
			props.onCancel();
			gsap.to(target.value, {
				x: 100,
				opacity: 0,
				onComplete: () => {
					closeSelf();
				},
			});
		}

		return {
			closeSelf,
			callOnConfirm,
			callOnCancel,
			target,
		};
	},
});
</script>
